import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, Settings, KeyRound, LogOut, ChevronDown, Sun, Moon } from 'lucide-react';
import { apiService, type User as UserType } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import DevTooltip from './DevTooltip';

interface UserMenuProps {
  currentUser: UserType;
  onLogout: () => void;
  onOpenProfile?: () => void;
  onOpenSettings?: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  currentUser,
  onLogout,
  onOpenProfile,
  onOpenSettings,
}) => {
  const user = currentUser;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme, themeMode, toggleTheme } = useTheme();

  // Validación defensiva
  if (!user) {
    return null;
  }

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Calcular posición del dropdown
  const getDropdownPosition = () => {
    if (!buttonRef.current) return { left: 0, top: 0 };

    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const spaceRight = window.innerWidth - rect.right;
    const dropdownWidth = 288; // w-72 = 18rem = 288px
    const estimatedHeight = 300;

    // Determinar si abrir hacia arriba o abajo
    const shouldOpenUp =
      spaceBelow < estimatedHeight || (spaceAbove > spaceBelow && spaceAbove > 150);

    // Calcular posición horizontal
    let left = rect.right - dropdownWidth;
    // Asegurar que no se salga de la pantalla
    left = Math.max(8, Math.min(left, window.innerWidth - dropdownWidth - 8));

    if (shouldOpenUp) {
      // Abrir hacia arriba
      const availableSpace = spaceAbove - 20;
      return {
        position: 'fixed' as const,
        left: `${left}px`,
        bottom: `${window.innerHeight - rect.top + 8}px`,
        top: 'auto',
        maxHeight: `${Math.min(availableSpace, 400)}px`,
        width: `${dropdownWidth}px`,
      };
    }

    // Abrir hacia abajo
    const availableSpace = spaceBelow - 20;
    return {
      position: 'fixed' as const,
      left: `${left}px`,
      top: `${rect.bottom + 8}px`,
      bottom: 'auto',
      maxHeight: `${Math.min(availableSpace, 400)}px`,
      width: `${dropdownWidth}px`,
    };
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleLogout = async () => {
    setIsOpen(false);
    await apiService.logout();
    onLogout();
  };

  return (
    <div className="relative" ref={menuRef}>
      {/* User Button - Compact for Input Area */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`min-w-[32px] min-h-[32px] flex items-center gap-1.5 p-1.5 rounded-lg border ${theme.colors.border.primary} ${theme.colors.bg.secondary} ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.tertiary} transition-colors`}
        data-component="UserMenu.tsx"
        title={user.full_name || user.username}
        aria-label={`${getInitials(user.full_name || user.username)} — Menú de usuario`}
        aria-expanded={isOpen}
      >
        {/* Avatar */}
        <div className="w-6 h-6 rounded-full bg-blue-600 dark:bg-blue-700 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.full_name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            getInitials(user.full_name || user.username)
          )}
        </div>

        {/* Chevron */}
        <ChevronDown
          className={`w-4 h-4 flex-shrink-0 transition-transform ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu - Using portal for better positioning */}
      {isOpen &&
        buttonRef.current &&
        createPortal(
          <div
            ref={dropdownRef}
            className={`fixed w-72 ${theme.colors.bg.secondary} rounded-lg shadow-lg border ${theme.colors.border.primary} py-2 z-[9999] overflow-y-auto`}
            style={getDropdownPosition()}
          >
            {/* User Info Header */}
            <div className={`px-4 py-3 border-b ${theme.colors.border.primary}`}>
              <p className={`text-sm font-medium ${theme.colors.text.primary}`}>
                {user.full_name || user.username}
              </p>
              <p className={`text-xs ${theme.colors.text.secondary} truncate`}>{user.email}</p>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              {onOpenProfile && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onOpenProfile();
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${theme.colors.text.primary} ${theme.colors.state.hover} transition-colors`}
                >
                  <User className="w-4 h-4" />
                  <span>Mi Perfil</span>
                </button>
              )}

              {onOpenSettings && (
                <button
                  onClick={() => {
                    setIsOpen(false);
                    onOpenSettings();
                  }}
                  className={`w-full flex items-center space-x-3 px-4 py-2 text-sm ${theme.colors.text.primary} ${theme.colors.state.hover} transition-colors`}
                >
                  <Settings className="w-4 h-4" />
                  <span>Configuración</span>
                </button>
              )}

              <div className={`border-t ${theme.colors.border.primary} my-2`}></div>

              {/* Toggle de Tema */}
              <button
                onClick={() => {
                  toggleTheme();
                  // No cerramos el menú para que vean el cambio inmediato
                }}
                className={`w-full flex items-center justify-between px-4 py-2 text-sm ${theme.colors.text.primary} ${theme.colors.state.hover} transition-colors`}
              >
                <div className="flex items-center space-x-3">
                  {themeMode === 'light' ? (
                    <Sun className="w-4 h-4" />
                  ) : (
                    <Moon className="w-4 h-4" />
                  )}
                  <span>Tema</span>
                </div>
                <span className={`text-xs ${theme.colors.text.secondary}`}>
                  {themeMode === 'light' ? 'Claro' : 'Oscuro'}
                </span>
              </button>

              <div className={`border-t ${theme.colors.border.primary} my-2`}></div>

              <button
                onClick={handleLogout}
                aria-label="Cerrar sesión"
                className="w-full flex items-center space-x-3 px-4 py-2 min-h-[44px] text-sm text-nb-error hover:bg-nb-error/10 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Cerrar Sesión</span>
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default UserMenu;
